import { useMainStore } from "@/stores/main";
import jspreadsheet, {
  CellValue,
  SpreadsheetInstance,
  WorksheetInstance,
} from "jspreadsheet-ce";

export const getSpreadsheetInstance = (): SpreadsheetInstance | null => {
  const spreadSheet = jspreadsheet.spreadsheet;
  if (!spreadSheet) return null;
  spreadSheet.map((sheet, index) => {
    if (index !== spreadSheet.length - 1) {
      jspreadsheet.destroy(sheet.element);
    }
  });
  return spreadSheet[spreadSheet.length - 1];
};

export const getWorksheetInstance = (): WorksheetInstance | null => {
  const spreadsheet = getSpreadsheetInstance();
  if (!spreadsheet) return null;
  const activeSheet = spreadsheet.getWorksheetActive();
  const worksheet = spreadsheet.worksheets[activeSheet] || null;
  return worksheet;
};

const normalizeWorksheetCellAddress = (address: string): string => {
  const [rangeStart] = address.split(":");
  const [, cellAddress = rangeStart] = rangeStart.split("!");
  return cellAddress.trim();
};

export const getCellElementSafely = (
  worksheet: WorksheetInstance | null,
  address: string,
): HTMLElement | null => {
  if (!worksheet) return null;

  try {
    return worksheet.getCell(normalizeWorksheetCellAddress(address));
  } catch {
    return null;
  }
};

export const getActiveWorksheetName = (): string | null => {
  return getWorksheetInstance()?.options.worksheetName || null;
};

export const getActiveWorksheetId = (): string | null => {
  return getWorksheetInstance()?.options.worksheetId || null;
};

export const getSelectedCell = (): {
  address: string;
  value: CellValue | null;
} | null => {
  const worksheet = getWorksheetInstance();
  if (!worksheet) return null;

  const selected = worksheet.selectedCell;
  if (selected) {
    const address = convertCoordinatesToAddress(
      selected[0] as number,
      selected[1] as number,
    );
    const value = worksheet.getValue(address);
    return { address, value };
  }
  return null;
};

export const getCellValue = (address: string): CellValue | null => {
  const worksheet = getWorksheetInstance();
  if (!worksheet) return null;
  return worksheet.getValue(address);
};

export const getRangeValues = (range: string): any[][] => {
  const [start, end] = range.split(":");
  // Handle both Excel-style (A1:B2) and coordinate-style (0,0:1,1) ranges
  let startCol: number, startRow: number, endCol: number, endRow: number;

  if (start.includes(",")) {
    // Coordinate-style format
    [startCol, startRow] = start.split(",").map(Number);
    [endCol, endRow] = end ? end.split(",").map(Number) : [startCol, startRow];
  } else {
    // Excel-style format
    const startCoords = convertAddressToCoordinates(start);
    const endCoords = end ? convertAddressToCoordinates(end) : startCoords;
    startCol = startCoords.col;
    startRow = startCoords.row;
    endCol = endCoords.col;
    endRow = endCoords.row;
  }

  const values: any[][] = [];

  for (let row = startRow; row <= endRow; row++) {
    const rowData: any[] = [];
    for (let col = startCol; col <= endCol; col++) {
      const value = getCellValue(convertCoordinatesToAddress(col, row));
      rowData.push(value);
    }
    values.push(rowData);
  }
  return values;
};

export const getMergedRanges = (): string[] => {
  const worksheet = getWorksheetInstance();
  if (!worksheet) return [];

  const merges = worksheet.getMerge();
  if (!merges) return [];

  const ranges: string[] = [];

  for (const [startAddress, [colSpan, rowSpan]] of Object.entries(merges)) {
    const startCoords = convertAddressToCoordinates(startAddress);
    const endCoords = {
      col: startCoords.col + colSpan - 1,
      row: startCoords.row + rowSpan - 1,
    };

    const endAddress = convertCoordinatesToAddress(
      endCoords.col,
      endCoords.row,
    );
    ranges.push(`${startAddress}:${endAddress}`);
  }

  return ranges;
};

export const convertColumnLetterToNumber = (letter: string): number => {
  let result = 0;
  for (let i = 0; i < letter.length; i++) {
    result = result * 26 + letter.charCodeAt(i) - "A".charCodeAt(0) + 1;
  }
  return result - 1;
};

export const convertNumberToColumnLetter = (num: number): string => {
  let letter = "";
  while (num >= 0) {
    letter = String.fromCharCode((num % 26) + "A".charCodeAt(0)) + letter;
    num = Math.floor(num / 26) - 1;
  }
  return letter;
};

export const convertAddressToCoordinates = (
  address: string,
): { col: number; row: number } => {
  const match = address.match(/^([A-Z]+)(\d+)$/);
  if (!match) {
    throw new Error(`Invalid cell address: ${address}`);
  }
  const col = convertColumnLetterToNumber(match[1]);
  const row = parseInt(match[2], 10) - 1;
  return { col, row };
};

export const convertCoordinatesToAddress = (
  col: number,
  row: number,
): string => {
  return `${convertNumberToColumnLetter(col)}${row + 1}`;
};

export const useRestoreSelection = () => {
  const currentSelection = useMainStore((state) => state.selection.current);

  const restoreSelection = () => {
    const worksheet = getWorksheetInstance();
    if (!worksheet) return;
    if (!currentSelection) {
      worksheet.updateSelectionFromCoords(0, 0);
      return;
    }
    if (currentSelection.address.includes(":")) {
      const [start, end] = currentSelection.address.split(":");
      const startCoords = convertAddressToCoordinates(start);
      const endCoords = convertAddressToCoordinates(end);
      worksheet.updateSelectionFromCoords(
        startCoords.col,
        startCoords.row,
        endCoords.col,
        endCoords.row,
      );
      return;
    } else {
      const coords = convertAddressToCoordinates(currentSelection.address);
      worksheet.updateSelectionFromCoords(coords.col, coords.row);
    }
  };

  return {
    restoreSelection,
  };
};
